export const INCOMING_ORDER_PREP_OPTIONS = [15, 30, 45, 60];

export function snapToIncomingPrepOption(raw) {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 30;
  if (INCOMING_ORDER_PREP_OPTIONS.includes(n)) return n;
  return INCOMING_ORDER_PREP_OPTIONS.reduce(
    (best, opt) => (Math.abs(opt - n) < Math.abs(best - n) ? opt : best),
    INCOMING_ORDER_PREP_OPTIONS[0],
  );
}

export function formatRestaurantOrderNumber(orderNumber) {
  return String(orderNumber || '').replace(/^POS-/i, '') || '--';
}

export function formatRestaurantOrderDateTime(isoString) {
  if (!isoString) return '--';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRestaurantCurrencyAmount(value, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export function getRestaurantOrderTypeLabel(orderType) {
  if (orderType === 'room_service') return 'Room Service';
  if (orderType === 'dine_in') return 'Dine In';
  if (orderType === 'takeaway') return 'Takeaway';
  return 'Order';
}

export function normalizeVariationId(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.floor(parsed);
}

export function getOrderItemIdentityKey(row = {}) {
  const menuItemId = Number(row?.menu_item || 0);
  const variationId = normalizeVariationId(row?.variation_id);
  return `${menuItemId}:${variationId || 0}`;
}

export function normalizeQueueTimers(timers = []) {
  return (Array.isArray(timers) ? timers : [])
    .filter((timer) => timer && typeof timer === 'object')
    .map((timer, index) => ({
      timer_id: timer.timer_id || `timer-${index + 1}`,
      started_at: timer.started_at || null,
      ended_at: timer.ended_at || null,
      target_minutes: Math.max(10, Number(timer.target_minutes || 30)),
    }));
}

export function isQrBasedIncomingOrder(order) {
  if (!order || typeof order !== 'object') return false;
  if (order?.is_open_qr_order) return true;
  const sourceCandidates = [
    order?.source,
    order?.order_source,
    order?.created_via,
    order?.origin,
    order?.channel,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);
  if (sourceCandidates.some((value) => value.includes('qr') || value.includes('guest'))) {
    return true;
  }
  return String(order?.status || '').toLowerCase() === 'draft';
}

export function getIncomingOrderSignature(order) {
  const orderId = Number(order?.id || 0);
  if (!orderId) return '';
  const itemSignature = (Array.isArray(order?.items) ? order.items : [])
    .map((item) => [
      Number(item?.menu_item || item?.menu_item_id || 0),
      normalizeVariationId(item?.variation_id) || '',
      Number(item?.quantity || 0),
      String(item?.special_note || '').trim(),
    ].join('|'))
    .sort()
    .join(',');
  return [orderId, itemSignature].join(':');
}

export function getIncomingOpenOrders(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((order) => {
      const status = String(order?.status || '').toLowerCase();
      if (status !== 'draft' && status !== 'placed') return false;
      return isQrBasedIncomingOrder(order);
    })
    .sort((first, second) => {
      const firstTs = new Date(first?.created_at || 0).getTime();
      const secondTs = new Date(second?.created_at || 0).getTime();
      return (Number.isFinite(firstTs) ? firstTs : 0) - (Number.isFinite(secondTs) ? secondTs : 0);
    });
}

let globalQrOrderNotifierActive = false;

const DISMISSED_POPUP_STORAGE_PREFIX = 'roomnexa_qr_popup_dismissed_';

export function getDismissedQrPopupOrderIds(hotelId) {
  if (!hotelId) return new Set();
  try {
    const raw = sessionStorage.getItem(`${DISMISSED_POPUP_STORAGE_PREFIX}${hotelId}`);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(
      (Array.isArray(parsed) ? parsed : [])
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0),
    );
  } catch {
    return new Set();
  }
}

export function markQrPopupOrdersDismissed(hotelId, orderIds = []) {
  if (!hotelId) return;
  const normalizedIds = (Array.isArray(orderIds) ? orderIds : [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0);
  if (!normalizedIds.length) return;
  try {
    const existing = getDismissedQrPopupOrderIds(hotelId);
    normalizedIds.forEach((id) => existing.add(id));
    sessionStorage.setItem(
      `${DISMISSED_POPUP_STORAGE_PREFIX}${hotelId}`,
      JSON.stringify(Array.from(existing)),
    );
  } catch {
    // Ignore storage failures.
  }
}

export function setGlobalQrOrderNotifierActive(active) {
  globalQrOrderNotifierActive = Boolean(active);
}

export function isGlobalQrOrderNotifierActive() {
  return globalQrOrderNotifierActive;
}

export function playIncomingQrOrderAlertSound() {
  if (typeof window === 'undefined') return;
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const now = audioContext.currentTime;
    const gain = audioContext.createGain();
    gain.connect(audioContext.destination);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 5);

    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, now);
    oscillator.frequency.setValueAtTime(1040, now + 0.14);
    oscillator.connect(gain);
    oscillator.start(now);
    oscillator.stop(now + 5);
    oscillator.onended = () => {
      audioContext.close().catch(() => {});
    };
  } catch {
    // Ignore autoplay restrictions.
  }
}
